import classNames from "classnames";
import React, { useEffect, useMemo, useRef, useState } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Autocomplete as MaterialAutocomplete } from "@material-ui/lab";
import { Checkbox, Text } from "..";
import TextField from "@material-ui/core/TextField";
import styles from "./dropdown-paginatedv2.module.scss";
import { debounced } from "utils";
import { useMount } from "hooks";

const DropdownPaginatedV2 = ({
  name,
  value,
  label,
  onChange,
  request,
  loading,
  total,
  formatRequest,
  placeholder,
  isMultiple,
  allLabel,
  allSelectedText,
  selectAllLabel,
  className,
  isAll,
  options = [],
  allOption = [],
  ...props
}) => {
  const [data, setData] = useState(options);
  const [page, setPage] = useState(1);
  const [searchKey, setSearchKey] = useState("");
  const [searching, setSearching] = useState("");
  const listRef = useRef();

  const autocompleteRef = useRef();
  //eslint-disable-next-line
  useEffect(
    debounced(async () => {
      setSearching(searchKey);
      const r = await request({
        page: 1,
        searchKey,
      });
      setData([...formatRequest(r)]);
      setPage(1);
      setSearching("");
    }, 1000),
    [searchKey],
  );

  const renderData = useMemo(() => {
    const newData = data.reduce((prev, curr) => {
      if (!prev.some((obj) => obj.label === curr.label && obj.value === curr.value)) {
        prev.push(curr);
      }
      return prev;
    }, []);

    const base = searchKey
      ? newData.filter((d) => d.label.toLowerCase().includes(searchKey.toLowerCase()))
      : [...newData];

    const withAllOption = isMultiple
      ? [{ label: selectAllLabel, value: "__all__" }, ...allOption, ...base]
      : [...allOption, ...base];

    const withLoadingIndicator = loading
      ? [...withAllOption, { label: "Loading...", value: "__loading__" }]
      : withAllOption;

    return withLoadingIndicator;
  }, [searchKey, data, allOption, isMultiple, selectAllLabel, loading]);

  const renderPlaceholder = useMemo(() => {
    if (label) {
      return label;
    }
    return placeholder;
  }, [placeholder, label]);

  const fetchFirstPage = async () => {
    const r = await request({
      page: 1,
      searchKey: "",
    });
    setData([...formatRequest(r)]);
    setPage(1);
    setSearchKey("");
  };

  useMount(async () => {
    await fetchFirstPage();
  });

  const normalizedValue = useMemo(() => {
    if (isMultiple) {
      return Array.isArray(value) ? value : [];
    }

    if (value === null || value === undefined || value === "") {
      return null;
    }

    const match = renderData.find((option) => option.value === value);
    return match || { label, value };
  }, [isMultiple, value, renderData, label]);

  return (
    <div className={classNames(className, styles.container)}>
      <MaterialAutocomplete
        className={styles.autocomplete}
        ref={autocompleteRef}
        multiple={isMultiple}
        disableCloseOnSelect={isMultiple}
        options={renderData}
        value={normalizedValue}
        getOptionSelected={(option, selected) => {
          if (!option || !selected) return false;
          return option.value === selected.value;
        }}
        getOptionDisabled={(option) => option.value === "__loading__"}
        getOptionLabel={(option) => option?.label || ""}
        filterOptions={(opts) => opts}
        onChange={(event, selected) => {
          if (isMultiple) {
            const isSelectAllClick =
              Array.isArray(selected) && selected.some((s) => s.value === "__all__");
            if (isSelectAllClick) {
              const willSelectAll = !isAll;
              onChange(name, {
                value: [],
                isAll: willSelectAll,
              });
              return;
            }

            const isSelectingAllItems =
              Array.isArray(selected) && selected.length === total && searchKey === "";

            onChange(name, {
              value: isSelectingAllItems ? [] : selected || [],
              isAll: isSelectingAllItems,
            });
          } else {
            onChange(name, {
              value: selected?.value ?? null,
              label: selected?.label ?? "",
              ...selected,
            });
          }
        }}
        onInputChange={(_, newInputValue, reason) => {
          if (reason === "input") {
            setSearchKey(newInputValue);
          }
          if (reason === "clear") {
            setSearchKey("");
            fetchFirstPage();
          }
        }}
        onOpen={fetchFirstPage}
        ListboxProps={{
          ref: listRef,
          onScroll: async (e) => {
            if (
              !loading &&
              e.target.scrollTop + 10 > e.target.scrollHeight - e.target.offsetHeight &&
              total > data.length
            ) {
              const p = page + 1;
              const r = await request({
                page: p,
                searchKey,
              });

              setPage(p);
              setData([...data, ...formatRequest(r)]);
            }
          },
          style: { fontSize: "14px", maxHeight: 224, overflow: "auto" },
        }}
        renderInput={(params) => (
          <TextField {...params} placeholder={renderPlaceholder} variant="outlined" {...props} />
        )}
        renderOption={(option) => {
          if (option.value === "__loading__") {
            return (
              <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
                <CircularProgress className={styles.circle} size={18} />
              </div>
            );
          }

          if (isMultiple && option.value === "__all__") {
            return (
              <div className={classNames(styles.option, { [`${styles.selectedAll}`]: isAll })}>
                <Checkbox value={isAll} checked={isAll} />
                <div className={styles.allText}>{selectAllLabel}</div>
              </div>
            );
          }

          if (isMultiple) {
            const isChecked =
              isAll || (Array.isArray(value) && value.some((v) => v.value === option.value));
            return (
              <div className={styles.option}>
                <Checkbox value={isChecked} checked={isChecked} />
                {option.label}
              </div>
            );
          }

          return <div className={styles.option}>{option.label}</div>;
        }}
        renderTags={(selected) => {
          if (isMultiple && isAll) {
            return <span className={styles.selectedLength}>{allSelectedText}</span>;
          }
          return selected.map((tag) => tag.label).join(", ");
        }}
        loading={loading}
        loadingText={<Text subtitle>Loading...</Text>}
        noOptionsText={<Text subtitle>No Data.</Text>}
        openOnFocus
        includeInputInList
        selectOnFocus
      />
    </div>
  );
};

export default DropdownPaginatedV2;
